import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Home, BookOpen, CheckCircle2, Upload, Trophy, Megaphone,
  Sparkles, Heart, Flame, Calendar, FileText, Send, Lock,
} from "lucide-react";
import { toast } from "sonner";

const TAU = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/90b9622f20e196612703a7d7c1f1ec7fa5d672234dbbf161661a04db600423c2.png";
const DOVE = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/c25b4cad97374f0313103a62c4cd65558dedf4529ed70aa000a4345f7b49b82a.png";

const NAV = [
  { id: "overview", to: "/dashboard/estudiante", label: "Inicio", icon: Home },
  { id: "topics", to: "/dashboard/estudiante#temas", label: "Mis catequesis", icon: BookOpen },
  { id: "retos", to: "/dashboard/estudiante#retos", label: "Retos semanales", icon: Sparkles },
  { id: "tasks", to: "/dashboard/estudiante#tareas", label: "Mis tareas", icon: Upload },
  { id: "logros", to: "/dashboard/estudiante#logros", label: "Mis logros", icon: Trophy },
  { id: "avisos", to: "/dashboard/estudiante#avisos", label: "Avisos pastorales", icon: Megaphone },
];

const BADGES = [
  { id: "fe", name: "Discípulo", icon: Heart, min: 1, color: "bg-primary text-primary-foreground" },
  { id: "pan", name: "Amigo del Pan", icon: Flame, min: 5, color: "bg-secondary text-secondary-foreground" },
  { id: "tau", name: "Cruz Tau", icon: Sparkles, min: 10, color: "bg-accent text-accent-foreground" },
  { id: "pascua", name: "Pascua Plena", icon: Trophy, min: 15, color: "bg-primary text-primary-foreground shimmer-green" },
];

export default function StudentDashboard() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [retos, setRetos] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [uploadDialog, setUploadDialog] = useState({ open: false, topic: null });

  const level = user?.level || "primera_comunion";
  const levelLabel = level === "primera_comunion" ? "Primera Comunión" : "Confirmación";
  const accentImg = level === "primera_comunion" ? TAU : DOVE;

  useEffect(() => {
    const isApproved = user?.status === "approved" && user?.level;
    if (!isApproved) return;
    api.get(`/topics?level=${level}`).then((r) => setTopics(r.data)).catch(() => {});
    api.get(`/progress/me`).then((r) => setCompleted(r.data.completed_topic_ids || [])).catch(() => {});
    api.get(`/stats/me`).then((r) => setStats(r.data)).catch(() => {});
    api.get(`/daily-content`).then((r) => setDaily(r.data)).catch(() => {});
    api.get(`/announcements`).then((r) => setAnnouncements(r.data.slice(0, 5))).catch(() => {});
    api.get(`/retos`).then((r) => setRetos(r.data)).catch(() => {});
    api.get(`/submissions/me`).then((r) => setSubmissions(r.data)).catch(() => {});
  }, [level, user?.status, user?.level]);

  const toggleComplete = async (topicId) => {
    const isDone = completed.includes(topicId);
    await api.post("/progress", { topic_id: topicId, completed: !isDone });
    if (isDone) setCompleted(completed.filter((i) => i !== topicId));
    else setCompleted([...completed, topicId]);
    const r = await api.get("/stats/me");
    setStats(r.data);
    toast.success(isDone ? "Tema marcado como pendiente" : "¡Tema completado! ✨");
  };

  const total = stats?.[level]?.total || topics.length || 1;
  const done = stats?.[level]?.completed || 0;
  const pct = Math.round((done / total) * 100);

  const status = user?.status || "approved";
  if (status === "pending" || status === "rejected" || (status === "approved" && !user?.level)) {
    return (
      <DashboardLayout navItems={[NAV[0]]} role="student">
        <section className="max-w-2xl mx-auto py-10 text-center" data-testid="student-pending">
          <div className="inline-flex w-16 h-16 rounded-full bg-secondary text-secondary-foreground items-center justify-center mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-secondary mb-2">
            {status === "rejected" ? "Acceso no autorizado" : "Cuenta en revisión"}
          </p>
          <h1 className="font-display text-3xl md:text-4xl text-primary leading-tight">
            {status === "rejected"
              ? "Tu registro no fue aprobado"
              : "Tu cuenta está pendiente de aprobación"}
          </h1>
          <p className="text-foreground/70 mt-3">
            {status === "rejected"
              ? "Si crees que es un error, comunícate con la coordinación pastoral del colegio."
              : "Hola " + (user?.name || "") + ". La coordinación pastoral revisará tu registro y te asignará tu sacramento (Primera Comunión o Confirmación). Recibirás acceso a contenidos, quizzes y actividades muy pronto."}
          </p>
          <div className="mt-6 rounded-2xl border border-border p-5 text-left bg-muted/30">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/60 mb-2">Mientras tanto puedes visitar</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-foreground/80">
              <li><Link className="text-primary hover:underline" to="/">Inicio</Link></li>
              <li><Link className="text-primary hover:underline" to="/centenario">Centenario Franciscano</Link></li>
              <li><Link className="text-primary hover:underline" to="/oracion">Rinconcito de oración</Link></li>
              <li><Link className="text-primary hover:underline" to="/eucaristia">Celebración de la Eucaristía</Link></li>
            </ul>
          </div>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={NAV} role="student">
      {/* Hero */}
      <section className="mb-10 animate-fade-up" id="overview" data-testid="student-overview">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-2">Tu camino · {levelLabel}</p>
        <h1 className="font-display text-4xl md:text-5xl text-primary leading-tight">
          ¡Paz y bien, {user?.name?.split(" ")[0]}!
        </h1>
        <p className="text-foreground/70 mt-2">Sigamos creciendo en la fe paso a paso.</p>
      </section>

      {/* Stats row */}
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Card className="rounded-2xl bg-primary text-primary-foreground border-none" data-testid="stat-progress">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80">Mi progreso</p>
            <p className="font-display text-4xl mt-2">{pct}%</p>
            <Progress value={pct} className="mt-2 bg-white/20" />
            <p className="text-xs opacity-80 mt-2">{done} de {total} temas</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-2 border-secondary/30">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-secondary">Sacramento</p>
            <p className="font-display text-2xl text-primary mt-2">{levelLabel}</p>
            <p className="text-xs text-foreground/60 mt-2">Grupo: <strong>{user?.group_code || "—"}</strong></p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">Tareas enviadas</p>
            <p className="font-display text-4xl text-primary mt-2">{submissions.length}</p>
            <p className="text-xs text-foreground/60 mt-2">{submissions.filter(s => s.status === "revisado").length} revisadas</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl bg-secondary text-secondary-foreground border-none">
          <CardContent className="p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80">Insignias</p>
            <p className="font-display text-4xl mt-2">{BADGES.filter(b => done >= b.min).length}</p>
            <p className="text-xs opacity-80 mt-2">de {BADGES.length} disponibles</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily */}
      {daily && (
        <div className="grid md:grid-cols-2 gap-5 mb-12" data-testid="daily-cards">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-primary flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5" /> Oración del día
              </p>
            </CardHeader>
            <CardContent>
              <p className="font-display text-2xl leading-snug">“{daily.verse.text}”</p>
              <p className="mt-3 text-sm font-semibold text-secondary">{daily.verse.ref}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl bg-accent text-accent-foreground border-none">
            <CardHeader className="pb-2">
              <p className="text-xs tracking-[0.2em] uppercase font-bold opacity-80 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Frase de San Francisco
              </p>
            </CardHeader>
            <CardContent>
              <p className="font-display text-2xl leading-snug">“{daily.francis_quote}”</p>
              <p className="mt-3 text-sm opacity-70">— San Francisco de Asís</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mi Proceso de Fe (temas) */}
      <section className="mb-14" id="temas" data-testid="student-topics">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Mi Proceso de Fe</p>
            <h2 className="font-display text-3xl text-primary">Temas de {levelLabel}</h2>
          </div>
          <img src={accentImg} alt="" className="w-16 h-16 animate-float opacity-80" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="topics-grid">
          {topics.map((t, i) => {
            const isDone = completed.includes(t.id);
            return (
              <Card key={t.id} className={`rounded-2xl hover:-translate-y-1 hover:shadow-md transition-all ${t.locked ? "locked" : ""}`} data-testid={`stopic-${t.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                      {t.cycle === "encuentro" ? "Encuentro" : t.cycle === "sacramental" ? "Sacramental" : "Franciscano"}
                    </Badge>
                    {isDone && <CheckCircle2 className="w-5 h-5 text-primary" />}
                    {t.locked && <Lock className="w-4 h-4 text-foreground/40" />}
                  </div>
                  <p className="text-xs text-foreground/60 flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.date_label}</p>
                  <h3 className="font-display text-xl text-primary mt-2 leading-tight line-clamp-2">{t.title}</h3>
                  <p className="text-xs text-secondary font-semibold mt-1">{t.bible_ref}</p>
                  <p className="text-sm text-foreground/70 mt-3 line-clamp-2">{t.focus}</p>
                  <div className="mt-4 flex gap-2">
                    <Button asChild size="sm" variant="outline" className="rounded-full text-xs" data-testid={`view-${t.id}`}>
                      <Link to={`/tema/${t.id}`}>Ver tema</Link>
                    </Button>
                    <Button size="sm" onClick={() => toggleComplete(t.id)} className="rounded-full text-xs" data-testid={`done-${t.id}`}>
                      {isDone ? "✓ Completado" : "Marcar"}
                    </Button>
                    <Button size="sm" variant="secondary" className="rounded-full text-xs px-3" onClick={() => setUploadDialog({ open: true, topic: t })} data-testid={`submit-${t.id}`}>
                      <Upload className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Retos semanales */}
      <section className="mb-14" id="retos" data-testid="student-retos">
        <p className="text-xs tracking-[0.3em] uppercase text-secondary font-bold mb-1">Mi Camino Franciscano</p>
        <h2 className="font-display text-3xl text-primary mb-6">Retos espirituales semanales</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {retos.map((r) => (
            <Card key={r.id} className="rounded-2xl bg-card hover:shadow-md transition-all hover:-translate-y-1" data-testid={`reto-${r.week}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex w-10 h-10 rounded-full bg-secondary text-secondary-foreground items-center justify-center font-display text-lg font-bold">{r.week}</span>
                  <Badge className="bg-primary/10 text-primary uppercase text-[10px] tracking-wider">{r.category}</Badge>
                </div>
                <h3 className="font-display text-xl text-primary leading-tight">{r.title}</h3>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{r.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Mis tareas */}
      <section className="mb-14" id="tareas" data-testid="student-tasks">
        <h2 className="font-display text-3xl text-primary mb-6">Mis tareas enviadas</h2>
        {submissions.length === 0 ? (
          <Card className="rounded-2xl">
            <CardContent className="p-10 text-center">
              <Send className="w-10 h-10 mx-auto text-foreground/40 mb-3" />
              <p className="text-foreground/70">Aún no has enviado tareas. Toca el botón <Upload className="w-3 h-3 inline" /> en cualquier tema para subir tu actividad.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => {
              const topic = topics.find(t => t.id === s.topic_id);
              return (
                <Card key={s.id} className="rounded-2xl" data-testid={`sub-${s.id}`}>
                  <CardContent className="p-5 flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-xs tracking-[0.2em] uppercase font-bold text-secondary">{topic?.date_label || ""}</p>
                      <h3 className="font-display text-lg text-primary">{topic?.title || s.topic_id}</h3>
                      {s.note && <p className="text-sm text-foreground/70 mt-2 italic">“{s.note}”</p>}
                      {s.feedback && (
                        <div className="mt-3 p-3 rounded-xl bg-secondary/10 border-l-4 border-secondary">
                          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-secondary">Comentario del catequista</p>
                          <p className="text-sm text-foreground/80 mt-1">{s.feedback}</p>
                          {s.grade && <p className="text-sm font-bold text-primary mt-1">Nota: {s.grade}</p>}
                        </div>
                      )}
                    </div>
                    <Badge className={s.status === "revisado" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70"}>
                      {s.status === "revisado" ? "Revisado" : "Pendiente"}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Logros */}
      <section className="mb-14" id="logros">
        <h2 className="font-display text-3xl text-primary mb-6">Insignias espirituales</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {BADGES.map((b) => {
            const Icon = b.icon;
            const unlocked = done >= b.min;
            return (
              <Card key={b.id} className={`rounded-2xl text-center ${unlocked ? "" : "opacity-50"}`} data-testid={`badge-${b.id}`}>
                <CardContent className="p-6">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${unlocked ? b.color : "bg-muted"} ${unlocked && b.color.includes("shimmer") ? "" : ""}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <p className="font-display text-lg text-primary mt-3">{b.name}</p>
                  <p className="text-xs text-foreground/60 mt-1">{unlocked ? "¡Desbloqueada!" : `${b.min - done} temas más`}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Avisos */}
      <section id="avisos">
        <h2 className="font-display text-3xl text-primary mb-6">Avisos pastorales</h2>
        <div className="space-y-3">
          {announcements.map((a) => (
            <Card key={a.id} className="rounded-2xl" data-testid={`s-ann-${a.id}`}>
              <CardContent className="p-5">
                <Badge className="bg-secondary text-secondary-foreground uppercase text-[10px] tracking-wider mb-2">{a.audience}</Badge>
                <h3 className="font-display text-xl text-primary">{a.title}</h3>
                <p className="text-sm text-foreground/70 mt-2 leading-relaxed">{a.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Submit dialog */}
      <SubmitDialog
        open={uploadDialog.open}
        topic={uploadDialog.topic}
        onClose={() => setUploadDialog({ open: false, topic: null })}
        onSubmitted={async () => {
          const r = await api.get("/submissions/me");
          setSubmissions(r.data);
        }}
      />
    </DashboardLayout>
  );
}

function SubmitDialog({ open, topic, onClose, onSubmitted }) {
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!topic) return;
    setBusy(true);
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = up.data.file_id;
      }
      await api.post("/submissions", { topic_id: topic.id, note, file_id });
      toast.success("¡Tarea enviada! Tu catequista la revisará.");
      setNote(""); setFile(null);
      onClose();
      onSubmitted();
    } catch (e) {
      toast.error("No se pudo enviar la tarea");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">Enviar tarea</DialogTitle>
          <p className="text-sm text-foreground/70">{topic?.title}</p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs tracking-wider uppercase font-semibold">Comentario / reflexión</label>
            <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Cuéntame qué aprendiste, qué te tocó el corazón…" data-testid="sub-note" />
          </div>
          <div>
            <label className="text-xs tracking-wider uppercase font-semibold">Archivo (opcional)</label>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.mp4" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" data-testid="sub-file" />
            {file && <p className="text-xs text-foreground/60 mt-1">{file.name} ({Math.round(file.size / 1024)} KB)</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="sub-submit">{busy ? "Enviando…" : "Enviar tarea"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
